import { File, Reimbursement, ReimbursementDocument, type User } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import { useReimbursementCreateMutation } from '../../../hooks/react-query/useReimbursementQuery';
import {
  FileClientService,
  getMimeType,
  ReimbursementClientService,
  S3ClientService,
} from '../../../tools/helpers';
import { type ReimbursementCreateFormValues } from './reimbursementFormSchemas';

/** What the create form starts with: the request prefix, and the log's owner as the person. */
export const getDefaultReimbursementCreateValues = (
  reimbursementOwnerId: User['id'],
): ReimbursementCreateFormValues => ({
  description: 'Reimbursement Request For: ',
  amount: 0,
  jobNumber: 0,
  photo: [],
  userId: reimbursementOwnerId,
});

/**
 * Creating a reimbursement, as desktop's Reimbursement Apply does it: upload the photo to S3,
 * record the file, create the reimbursement as Submitted (status 2), then attach the photo to it.
 * Shared by the desktop dialog and the phone page so both send the same requests in the same order.
 */
export const useCreateReimbursement = ({
  loggedUserId,
  onCreated,
}: {
  loggedUserId: User['id'];
  /** After the reimbursement and its photo are saved, such as closing the form. */
  onCreated: () => void;
}) => {
  const queryClient = useQueryClient();
  const createReimbursementMutation = useReimbursementCreateMutation();
  const [isSaving, setIsSaving] = useState(false);

  const createReimbursement = async ({ data }: { data: ReimbursementCreateFormValues }) => {
    try {
      const file = data.photo.at(0);
      if (!file) return;

      setIsSaving(true);

      const name = `${Math.floor(Date.now() / 1000)}-${file.name}`;

      const fileString = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result === 'string') resolve(result);
          reject();
        };
        reader.readAsDataURL(file);
      });
      //Upload to S3 reimbursement

      const uploadRes = await S3ClientService.uploadFileToS3Bucket(
        name,
        fileString,
        'kalos-reimbursements',
      );
      if (uploadRes === 'nok') throw new Error('Error uploading file');

      //If successful, create file record
      const fileRes = await FileClientService.Create(
        File.create({
          bucket: 'kalos-reimbursements',
          name: name,
          mimeType: getMimeType(name),
        }),
      );
      if (fileRes) {
        //if Successfull, create reimbursement record
        const createdReimbursement = await createReimbursementMutation.mutateAsync(
          Reimbursement.create({
            description: data.description,
            amount: data.amount,
            statusId: 2,
            userId: data.userId,
            creatorUserId: loggedUserId,
            jobNumber: data.jobNumber,
          }),
        );

        await ReimbursementClientService.CreateDocument(
          ReimbursementDocument.create({
            description: name,
            fileId: fileRes.id,
            reimbursementId: createdReimbursement.id,
          }),
        );
        queryClient.invalidateQueries({ queryKey: [queryKeys.reimbursement.root] });
        toast({
          variant: 'success',
          title: 'Reimbursement created successfully',
        });
        onCreated();
      }
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error during creation of reimbursement',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return { createReimbursement, isSaving };
};
